import {
  AppShell,
  Badge,
  CommandPalette,
  Main,
  OfflineBanner,
  Skeleton,
  Spinner,
  Workspace,
  useCommandPaletteShortcut,
  type CommandGroup,
} from '@stayafrica/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Outlet, createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import {
  Ban,
  Banknote,
  BarChart3,
  BedDouble,
  BookOpen,
  Building2,
  CalendarDays,
  CalendarSearch,
  Coins,
  Download,
  FileText,
  LogOut,
  Moon,
  Receipt,
  Sun,
  UserPlus,
  Users,
  Wallet,
} from 'lucide-react';
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SEARCH_DEBOUNCE_MS,
  SEARCH_MIN_LENGTH,
  globalSearchQueryOptions,
} from '@/features/analytics/queries';
import { logout, meQueryOptions } from '@/features/auth/queries';
import { PaymentDrawer } from '@/features/payments/PaymentDrawer';
import { allowedSearchTypes, groupHits, searchTarget } from '@/features/search/hits';
import { SessionProvider, useSession } from '@/features/auth/session';
import { AppSidebar } from '@/features/shell/AppSidebar';
import { AppTopbar } from '@/features/shell/AppTopbar';
import { NAV, isNavVisible } from '@/features/shell/nav';
import { useOnlineStatus } from '@/lib/online';
import { readStorage, writeStorage } from '@/lib/storage';
import { useTheme } from '@/lib/theme';

const COLLAPSE_KEY = 'sa.sidebar.collapsed';

/** Repère visuel par type de résultat de la recherche globale (SCR-140). */
const SEARCH_ICONS = {
  RESERVATION: BookOpen,
  CUSTOMER: Users,
  COMPANY: Building2,
  INVOICE: Receipt,
  PROFORMA: FileText,
  PAYMENT: Banknote,
  RESOURCE: BedDouble,
  EXPENSE: Wallet,
} as const;

export const Route = createFileRoute('/_app')({
  beforeLoad: async ({ context, location }) => {
    const user = await context.queryClient.ensureQueryData(meQueryOptions());
    if (!user) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack Router utilise une exception de redirection
      throw redirect({
        to: '/login',
        search: { redirect: location.href },
      });
    }
  },
  component: AppLayout,
});

function AppLayout() {
  return (
    <Suspense fallback={<ShellSkeleton />}>
      <SessionProvider>
        <AuthenticatedShell />
      </SessionProvider>
    </Suspense>
  );
}

function ShellSkeleton() {
  return (
    <div className="main" aria-busy="true">
      <div className="container stack">
        <Skeleton variant="title" />
        <Skeleton height={120} />
        <Skeleton height={320} />
      </div>
    </div>
  );
}

function AuthenticatedShell() {
  const { t } = useTranslation(['nav', 'search', 'reports', 'common']);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const session = useSession();
  const { theme, toggle: toggleTheme } = useTheme();
  const online = useOnlineStatus();

  const [collapsed, setCollapsed] = useState(() => readStorage(COLLAPSE_KEY) === 'true');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [paletteSearch, setPaletteSearch] = useState('');
  const [paletteTerm, setPaletteTerm] = useState('');
  // Tiroir « Enregistrer un paiement » ouvert depuis ⌘K, sans contexte.
  const [paymentDrawerOpen, setPaymentDrawerOpen] = useState(false);

  // Recherche globale (barre de la topbar et ⌘K) : un seul appel
  // `GET /search`, anti-rebond 200 ms, à partir de deux caractères (SCR-140).
  useEffect(() => {
    const timer = window.setTimeout(() => {
      const trimmed = paletteSearch.trim();
      setPaletteTerm(trimmed.length >= SEARCH_MIN_LENGTH ? trimmed : '');
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [paletteSearch]);

  const searchTypes = useMemo(
    () => allowedSearchTypes((permission) => session.can(permission)),
    [session],
  );
  const searchQuery = useQuery(
    globalSearchQueryOptions(paletteTerm, {
      types: searchTypes,
      limit: 20,
      enabled: paletteOpen && searchTypes.length > 0,
    }),
  );

  useEffect(() => {
    writeStorage(COLLAPSE_KEY, String(collapsed));
  }, [collapsed]);

  // Raccourci « [ » : replier / déplier la barre latérale (UX_ARCHITECTURE §6.2).
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const editable =
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.isContentEditable === true;
      if (!editable && event.key === '[' && !event.metaKey && !event.ctrlKey) {
        setCollapsed((value) => !value);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const openPalette = useCallback(() => setPaletteOpen(true), []);
  useCommandPaletteShortcut(openPalette);

  const handleLogout = useCallback(async () => {
    await logout();
    queryClient.setQueryData(['auth', 'me'], null);
    await queryClient.invalidateQueries();
    await navigate({ to: '/login' });
  }, [navigate, queryClient]);

  const navigationItems = NAV.flatMap((entry) => {
    if (!isNavVisible(entry, session)) return [];
    const Icon = entry.icon;
    const own = entry.to
      ? [
          {
            id: entry.key,
            label: t(entry.labelKey),
            icon: <Icon className="ico" aria-hidden="true" />,
            hint: entry.implemented === false ? t('nav:palette.soon') : undefined,
            onSelect: () => void navigate({ to: entry.to! }),
          },
        ]
      : [];
    const children = (entry.children ?? [])
      .filter((child) => isNavVisible(child, session))
      .map((child) => ({
        id: `${entry.key}.${child.key}`,
        label: `${t(entry.labelKey)} › ${t(child.labelKey)}`,
        icon: <Icon className="ico" aria-hidden="true" />,
        hint: child.implemented ? undefined : t('nav:palette.soon'),
        onSelect: () => void navigate({ to: child.to }),
      }));
    return [...own, ...children];
  });

  // Actions rapides, filtrées par permission (UX_ARCHITECTURE §11).
  const quickActions = [
    session.can('reservation.create')
      ? {
          id: 'reservation.create',
          label: t('nav:palette.actionCreateReservation'),
          icon: <BookOpen className="ico" aria-hidden="true" />,
          onSelect: () => void navigate({ to: '/reservations/new' }),
        }
      : null,
    session.can('customer.create')
      ? {
          id: 'customer.create',
          label: t('nav:palette.actionCreateCustomer'),
          icon: <UserPlus className="ico" aria-hidden="true" />,
          onSelect: () => void navigate({ to: '/customers/people', search: { new: 'customer' } }),
        }
      : null,
    session.can('resource.create')
      ? {
          id: 'resource.create',
          label: t('nav:palette.actionCreateResource'),
          icon: <BedDouble className="ico" aria-hidden="true" />,
          onSelect: () => void navigate({ to: '/resources', search: { new: 'resource' } }),
        }
      : null,
    session.can('availability.block')
      ? {
          id: 'block.create',
          label: t('nav:palette.actionCreateBlock'),
          icon: <Ban className="ico" aria-hidden="true" />,
          onSelect: () => void navigate({ to: '/resources/maintenance', search: { new: 'block' } }),
        }
      : null,
    session.can('availability.read')
      ? {
          id: 'calendar.open',
          label: t('nav:palette.actionOpenCalendar'),
          icon: <CalendarDays className="ico" aria-hidden="true" />,
          onSelect: () => void navigate({ to: '/calendar' }),
        }
      : null,
    session.can('proforma.create')
      ? {
          id: 'proforma.create',
          label: t('nav:palette.actionCreateProforma'),
          icon: <FileText className="ico" aria-hidden="true" />,
          onSelect: () => void navigate({ to: '/billing/proformas', search: { new: 'proforma' } }),
        }
      : null,
    session.can('payment.record')
      ? {
          id: 'payment.record',
          label: t('nav:palette.actionRecordPayment'),
          icon: <Banknote className="ico" aria-hidden="true" />,
          onSelect: () => setPaymentDrawerOpen(true),
        }
      : null,
    session.can('expense.record')
      ? {
          id: 'expense.record',
          label: t('nav:palette.actionRecordExpense'),
          keywords: ['depense', 'dépense', 'expense'],
          icon: <Coins className="ico" aria-hidden="true" />,
          onSelect: () => void navigate({ to: '/finance/expenses', search: { new: 'expense' } }),
        }
      : null,
    session.can('cash.read')
      ? {
          id: 'cash.open',
          label: t('nav:palette.actionOpenCash'),
          icon: <Wallet className="ico" aria-hidden="true" />,
          onSelect: () => void navigate({ to: '/billing/cash' }),
        }
      : null,
    session.can('payment.read')
      ? {
          id: 'payment.search',
          label: t('nav:palette.actionSearchPayment'),
          icon: <Banknote className="ico" aria-hidden="true" />,
          onSelect: () => void navigate({ to: '/billing/payments' }),
        }
      : null,
    session.can('invoice.read')
      ? {
          id: 'invoice.search',
          label: t('nav:palette.actionSearchInvoice'),
          icon: <Receipt className="ico" aria-hidden="true" />,
          onSelect: () => void navigate({ to: '/billing/invoices' }),
        }
      : null,
    session.can('report.operational')
      ? {
          id: 'reports.open',
          label: t('nav:palette.actionOpenReports'),
          icon: <BarChart3 className="ico" aria-hidden="true" />,
          onSelect: () => void navigate({ to: '/reports' }),
        }
      : null,
    session.can('report.export')
      ? {
          id: 'reports.export',
          label: t('nav:palette.actionExportReport'),
          keywords: ['csv', 'export'],
          icon: <Download className="ico" aria-hidden="true" />,
          hint: t('nav:palette.actionExportReportHint'),
          onSelect: () => void navigate({ to: '/reports', search: { export: true } }),
        }
      : null,
    session.can('availability.read')
      ? {
          id: 'availability.search',
          label: t('nav:palette.actionSearchAvailability'),
          icon: <CalendarSearch className="ico" aria-hidden="true" />,
          onSelect: () => void navigate({ to: '/resources/availability' }),
        }
      : null,
  ].filter((item): item is NonNullable<typeof item> => item !== null);

  // Résultats de recherche, groupés par type. Le terme saisi est ajouté aux
  // mots-clés : le filtre local de la palette ne doit jamais masquer une
  // réponse du serveur (référence `FAC/2026/00031`, numéro de téléphone…).
  const searchGroups: CommandGroup[] = groupHits(searchQuery.data?.hits ?? []).map((group) => ({
    id: `search-${group.type}`,
    heading: t(`search:groups.${group.type}`),
    items: group.hits.map((hit) => ({
      id: `${group.type}-${hit.id}`,
      label: hit.reference ? `${hit.reference} — ${hit.title}` : hit.title,
      keywords: [paletteTerm, hit.reference ?? '', hit.subtitle ?? ''],
      icon: (() => {
        const Icon = SEARCH_ICONS[group.type];
        return <Icon className="ico" aria-hidden="true" />;
      })(),
      ...(hit.subtitle ? { hint: hit.subtitle } : {}),
      ...(hit.status
        ? {
            badge: (
              <Badge tone="neutral">
                {t(`search:status.${hit.status}`, { defaultValue: hit.status })}
              </Badge>
            ),
          }
        : {}),
      onSelect: () => void navigate(searchTarget(hit)),
    })),
  }));

  const groups: CommandGroup[] = [
    ...searchGroups,
    ...(quickActions.length > 0
      ? [{ id: 'actions', heading: t('nav:palette.groupActions'), items: quickActions }]
      : []),
    { id: 'navigation', heading: t('nav:palette.groupNavigation'), items: navigationItems },
    {
      id: 'preferences',
      heading: t('nav:palette.groupPreferences'),
      items: [
        {
          id: 'theme',
          label: t('nav:palette.actionToggleTheme'),
          icon:
            theme === 'dark' ? (
              <Moon className="ico" aria-hidden="true" />
            ) : (
              <Sun className="ico" aria-hidden="true" />
            ),
          onSelect: toggleTheme,
        },
        {
          id: 'logout',
          label: t('nav:palette.actionLogout'),
          icon: <LogOut className="ico" aria-hidden="true" />,
          onSelect: () => void handleLogout(),
        },
      ],
    },
  ];

  return (
    <AppShell collapsed={collapsed} mobileNavOpen={mobileNavOpen}>
      <a className="skip-link" href="#main">
        {t('nav:skipToContent')}
      </a>

      <AppSidebar
        collapsed={collapsed}
        onToggleCollapsed={() => setCollapsed((value) => !value)}
        onNavigate={() => setMobileNavOpen(false)}
      />
      {mobileNavOpen ? (
        <div className="mobile-scrim" role="presentation" onClick={() => setMobileNavOpen(false)} />
      ) : null}

      <Workspace>
        {!online ? <OfflineBanner>{t('common:states.offline')}</OfflineBanner> : null}
        <AppTopbar
          theme={theme}
          onToggleTheme={toggleTheme}
          onOpenPalette={openPalette}
          onToggleMobileNav={() => setMobileNavOpen((value) => !value)}
          onLogout={() => void handleLogout()}
        />
        <Main>
          <Outlet />
        </Main>
      </Workspace>

      <CommandPalette
        open={paletteOpen}
        onOpenChange={(open) => {
          setPaletteOpen(open);
          if (!open) {
            setPaletteSearch('');
            setPaletteTerm('');
          }
        }}
        search={paletteSearch}
        onSearchChange={setPaletteSearch}
        groups={groups}
        title={t('nav:palette.title')}
        placeholder={t('nav:palette.placeholder')}
        emptyLabel={
          paletteTerm.length >= SEARCH_MIN_LENGTH
            ? t('search:noResults', { query: paletteTerm })
            : t('nav:palette.empty')
        }
        shortcuts={{
          navigate: t('nav:palette.shortcutNavigate'),
          open: t('nav:palette.shortcutOpen'),
          close: t('nav:palette.shortcutClose'),
        }}
        status={
          paletteSearch.trim().length > 0 && paletteSearch.trim().length < SEARCH_MIN_LENGTH ? (
            t('search:tooShort', { count: SEARCH_MIN_LENGTH })
          ) : searchQuery.isFetching ? (
            <>
              <Spinner />
              {t('search:searching')}
            </>
          ) : paletteTerm.length >= SEARCH_MIN_LENGTH && searchQuery.data ? (
            t('search:resultCount', { count: searchQuery.data.hits.length })
          ) : null
        }
      />

      <PaymentDrawer open={paymentDrawerOpen} onOpenChange={setPaymentDrawerOpen} />
    </AppShell>
  );
}
